import { Add, Check, Clear, Edit } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Button, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { t } from 'i18next'
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import { FormState, UseFormReset, UseFormSetValue } from 'react-hook-form'
import { useSelector } from 'react-redux'
import AvatarComponent from '../../components/avatar/AvatarComponent'
import ProjectPicture from '../../components/avatar/ProjectPicture'
import { useEditMode } from '../../store/redux/hooks'
import { getCurrentUser, getIsAdmin, getIsBaseUser, getIsManagement } from '../../store/redux/reducers/currentUserSlice'
import { getSidebarState } from '../../store/redux/reducers/sidebarSlice'
import { User, UserFormValues } from '../../store/types'
import ProfileViewActions from './ProfileViewActions'

type ProfileViewHeaderProps = {
  user: User | null
  setFormValue: UseFormSetValue<UserFormValues>
  isEditMode: boolean
  setIsEditMode: (value: boolean) => void
  isNewUser: boolean
  isMyProfile: boolean
  isCurrentUserLeader: boolean | undefined
  handleCreate: () => void
  handleEdit: () => void
  resetForm: UseFormReset<UserFormValues>
  formState: FormState<UserFormValues>
  setIsCancelDialogOpen: Dispatch<SetStateAction<boolean>>
  checkUnsavedChanges: () => boolean
}

const ProfileViewHeader = ({
  user,
  setFormValue,
  isEditMode,
  setIsEditMode,
  isNewUser,
  isMyProfile,
  isCurrentUserLeader,
  handleCreate,
  handleEdit,
  resetForm,
  formState,
  setIsCancelDialogOpen,
  checkUnsavedChanges,
}: ProfileViewHeaderProps) => {
  const currentUser = useSelector(getCurrentUser)
  const isAdmin = useSelector(getIsAdmin)
  const isBaseUser = useSelector(getIsBaseUser)
  const isManagement = useSelector(getIsManagement)

  const [resetProfilePicture, setResetProfilePicture] = useState(false)
  const [avatar, setAvatar] = useState<string | undefined>(user?.avatar)
  const [extension, setExtension] = useState<string | undefined>(user?.extension)
  const setEditMode = useEditMode().setIsEditMode

  const handleUpdateAvatar = (newAvatar: string) => {
    setAvatar(newAvatar)
    setFormValue('avatar', newAvatar)
  }
  const handleUpdateExtension = (newExtension: string) => {
    setExtension(newExtension)
    setFormValue('extension', newExtension)
  }

  const handleEditButtonClick = () => {
    setIsEditMode(true)
    setEditMode(true)
  }

  const handleSaveButtonClick = () => {
    handleEdit()
  }

  const handleCreateButtonClick = () => {
    handleCreate()
  }

  const { isSubmitting } = formState

  const handleConfirmCancel = useCallback(() => {
    setResetProfilePicture(!resetProfilePicture)
    setIsEditMode(false)
    setEditMode(false)
    if (user && user.avatar && user.extension) {
      updateViewValues(user.avatar, user.extension)
    } else {
      updateViewValues(undefined, undefined)
    }
    resetForm()
  }, [resetProfilePicture, setIsEditMode, setEditMode, user, resetForm])

  const handleCancelButtonClick = () => {
    if (!checkUnsavedChanges()) {
      handleConfirmCancel()
    } else {
      setIsCancelDialogOpen(true)
    }
  }

  const theme = useTheme()

  const isSidebarOpen = useSelector(getSidebarState)

  const isXs = useMediaQuery(theme.breakpoints.down('sm'))
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'))
  const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg'))
  const isUpMd = useMediaQuery(theme.breakpoints.up('md'))
  const isDownMd = useMediaQuery(theme.breakpoints.down('md'))
  const isUpLg = useMediaQuery(theme.breakpoints.up('lg'))

  const getAvatarSize = (): number => {
    if (isXs || (isSm && isSidebarOpen)) return 150
    if ((isSm && !isSidebarOpen) || isMd) return 75
    return 100
  }

  const areActionButtonsVisible = currentUser?.id === user?.id || isManagement || isCurrentUserLeader
  const canEditProfile = !isBaseUser && (isManagement || isAdmin || isMyProfile || isCurrentUserLeader)

  const updateViewValues = (avatar?: string, extension?: string) => {
    setAvatar(avatar)
    setExtension(extension)
  }

  useEffect(() => {
    if (user && user.avatar && user.extension) {
      updateViewValues(user.avatar, user.extension)
    } else {
      updateViewValues(undefined, undefined)
    }
  }, [user])

  useEffect(() => {
    if (!isEditMode && user) {
      if (user.avatar && user.extension) {
        updateViewValues(user.avatar, user.extension)
      } else {
        updateViewValues(undefined, undefined)
      }
    }
  }, [isEditMode, user])

  return (
    <Stack
      direction={{ xs: 'column', sm: isSidebarOpen ? 'column' : 'row', md: 'row' }}
      justifyContent="space-between"
      alignItems={
        isBaseUser || isNewUser || isEditMode || !areActionButtonsVisible
          ? 'center'
          : { xs: 'center', sm: isSidebarOpen ? 'center' : 'flex-end', md: 'flex-end' }
      }
      gap={{ xs: '16px', lg: '24px', xl: '32px' }}
      px={2}
    >
      <Stack sx={{ width: 'fit-content' }}>
        {isNewUser && isEditMode ? (
          <ProjectPicture
            extension={extension}
            avatar={avatar}
            alt={user?.firstName}
            editable={isEditMode}
            updateAvatar={handleUpdateAvatar}
            updateExtension={handleUpdateExtension}
            onChange={updateViewValues}
          />
        ) : (
          user &&
          !isNewUser &&
          !isEditMode && (
            <AvatarComponent
              avatar={avatar}
              extension={extension}
              alt={user.firstName + ' ' + user.lastName}
              size={getAvatarSize()}
            />
          )
        )}
        {isEditMode && user && !isNewUser && (
          <ProjectPicture
            extension={extension}
            avatar={avatar}
            alt={user?.firstName}
            editable={isEditMode}
            updateAvatar={handleUpdateAvatar}
            updateExtension={handleUpdateExtension}
            onChange={updateViewValues}
          />
        )}
      </Stack>

      <Stack sx={{ width: '100%' }} direction="column" justifyContent="flex-start" overflow="hidden" gap="8px">
        <Stack
          sx={{ width: '100%', height: '100%', flex: 1 }}
          direction={
            !areActionButtonsVisible || isEditMode || isBaseUser || (isCurrentUserLeader && !isManagement)
              ? 'column'
              : { xs: 'column', md: isSidebarOpen ? 'column' : 'row', lg: 'row' }
          }
          textAlign={{ xs: 'center', sm: isSidebarOpen ? 'center' : 'left', md: 'left' }}
          alignItems={{ xs: 'center', sm: isSidebarOpen ? 'center' : 'baseline', md: 'baseline' }}
          gap={
            isEditMode || isBaseUser || !areActionButtonsVisible
              ? '4px'
              : { xs: '4px', md: isSidebarOpen ? 0 : '16px', lg: '16px' }
          }
        >
          <Typography
            component={'h2'}
            variant={isUpLg ? 'h4' : 'h5'}
            fontWeight={700}
            sx={{
              width: '100%',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              color: 'text.highEmphasis',
            }}
          >
            {isNewUser ? t('page.usersProfile.header.new') : `${user?.firstName} ${user?.lastName}`}
          </Typography>

          {(isBaseUser || isEditMode || isDownMd || (isMd && isSidebarOpen) || !areActionButtonsVisible) && (
            <Typography
              variant="caption"
              color="text.base"
              fontWeight={400}
              sx={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden', width: '100%' }}
            >
              {user?.email}
            </Typography>
          )}
        </Stack>

        {areActionButtonsVisible && !isEditMode && isUpMd && (
          <Stack sx={{ maxWidth: '500px' }}>
            {user && !isBaseUser && (
              <ProfileViewActions
                user={user}
                isMyProfile={isMyProfile}
                isManagement={isManagement}
                isCurrentUserLeader={isCurrentUserLeader}
              />
            )}
          </Stack>
        )}
      </Stack>

      {isNewUser ? (
        <Stack
          direction="row"
          width="fit-content"
          gap="16px"
          height="100%"
          justifyContent="flex-end"
          alignItems="flex-end"
        >
          <LoadingButton
            type="button"
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={handleCreateButtonClick}
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {t('page.usersProfile.header.saveUser')}
          </LoadingButton>
        </Stack>
      ) : (
        canEditProfile &&
        (isEditMode ? (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            width="fit-content"
            gap="16px"
            height="100%"
            justifyContent="flex-end"
            alignItems="flex-end"
          >
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Clear />}
              size="small"
              onClick={handleCancelButtonClick}
              disabled={isSubmitting}
              sx={{ width: { xs: '100%', sm: 'fit-content' } }}
            >
              {t('page.usersProfile.header.cancel')}
            </Button>
            <LoadingButton
              variant="contained"
              color="primary"
              startIcon={<Check />}
              size="small"
              onClick={handleSaveButtonClick}
              loading={isSubmitting}
              disabled={isSubmitting}
              sx={{ width: { xs: '100%', sm: 'fit-content' } }}
            >
              {t('page.usersProfile.header.saveUser')}
            </LoadingButton>
          </Stack>
        ) : (
          <Stack direction="row" sx={{ width: '120px' }} justifyContent="flex-end" height="100%">
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Edit />}
              size="small"
              onClick={handleEditButtonClick}
              sx={{ width: isMd ? '100px' : '100%', span: { marginRight: isMd ? 0 : '8px' } }}
            >
              {t('page.usersProfile.header.edit')}
            </Button>
          </Stack>
        ))
      )}
    </Stack>
  )
}

export default ProfileViewHeader
